import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft, CheckCircle2, LockKeyhole } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { changePasswordSchema, getPasswordStrength } from "@/lib/support-center";
import SEO from "@/components/SEO";

const ResetPassword = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isRecoveryLink, setIsRecoveryLink] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [updated, setUpdated] = useState(false);

  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const search = new URLSearchParams(window.location.search);
    const recoveryType = hash.get("type") || search.get("type");
    const hasToken = Boolean(hash.get("access_token")) || Boolean(search.get("access_token"));

    if (recoveryType === "recovery" || hasToken || user) {
      setIsRecoveryLink(true);
      setLinkError(null);
      return;
    }

    setIsRecoveryLink(false);
    setLinkError("This reset link is invalid or has expired. Request a fresh password reset email to continue.");
  }, [user]);

  const passwordStrength = useMemo(() => getPasswordStrength(newPassword), [newPassword]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = changePasswordSchema.safeParse({
      currentPassword: "TemporaryRecoveryValidation1",
      newPassword,
      confirmPassword,
    });

    if (!parsed.success) {
      const message = parsed.error.issues.find((issue) => issue.path[0] !== "currentPassword")?.message || "Choose a stronger password.";
      toast.error(message);
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });

    if (error) {
      setSubmitting(false);
      setLinkError(error.message);
      toast.error(error.message);
      return;
    }

    const currentUser = (await supabase.auth.getUser()).data.user;
    if (currentUser) {
      await supabase.from("account_security_events").insert({
        user_id: currentUser.id,
        event_type: "password_reset_completed",
        status: "completed",
        metadata: {
          recovery_flow: true,
          completed_at: new Date().toISOString(),
        },
      });
    }

    await supabase.auth.signOut();
    setSubmitting(false);
    setUpdated(true);
    toast.success("Password updated successfully.");
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-hero">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-hero px-4 py-10 sm:px-6">
      <SEO
        title="Reset your password — HeirWise"
        description="Set a new password for your HeirWise account and sign back in to keep your learning progress secure."
        path="/reset-password"
      />
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-8 md:flex-row md:items-start md:justify-between">
        <div className="max-w-xl space-y-4 text-primary-foreground">
          <Link to="/login" className="inline-flex items-center gap-2 text-sm text-primary-foreground/80 transition-colors hover:text-primary-foreground">
            <ArrowLeft className="h-4 w-4" />
            Back to sign in
          </Link>
          <p className="text-sm uppercase tracking-[0.24em] text-primary-foreground/60">HeirWise credential reset</p>
          <h1 className="font-heading text-4xl font-bold leading-tight">Create a fresh password you can trust.</h1>
          <p className="text-sm leading-7 text-primary-foreground/75 sm:text-base">
            Choose a strong new password, then sign back in to keep your HeirWise account secure.
          </p>
        </div>

        <div className="w-full max-w-md rounded-[2rem] border border-primary-foreground/15 bg-card/95 p-6 shadow-lg sm:p-8">
          {updated ? (
            <div className="space-y-5 text-center">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl border border-primary/30 bg-primary/10 text-primary">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <div className="space-y-2">
                <h2 className="font-heading text-2xl font-bold text-foreground">Password updated</h2>
                <p className="text-sm leading-6 text-muted-foreground">
                  Your password has been changed successfully. Sign back in with your new credentials to continue learning.
                </p>
              </div>
              <Button className="w-full" onClick={() => navigate("/login", { replace: true })}>
                Return to sign in
              </Button>
            </div>
          ) : !isRecoveryLink ? (
            <div className="space-y-4 rounded-3xl border border-destructive/25 bg-destructive/10 p-5 text-left">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 text-destructive" />
                <div className="space-y-2">
                  <h2 className="font-heading text-xl font-bold text-foreground">Reset link unavailable</h2>
                  <p className="text-sm leading-6 text-muted-foreground">{linkError}</p>
                </div>
              </div>
              <Button asChild className="w-full">
                <Link to="/forgot-password">Request a new reset link</Link>
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <p className="text-sm uppercase tracking-[0.22em] text-muted-foreground">Reset password</p>
                <h2 className="font-heading text-2xl font-bold text-foreground">Secure your account</h2>
                <p className="text-sm leading-6 text-muted-foreground">
                  Use a password with uppercase and lowercase letters plus at least one number.
                </p>
              </div>

              {linkError && (
                <div className="mt-4 rounded-2xl border border-destructive/25 bg-destructive/10 p-4 text-sm text-destructive">
                  {linkError}
                </div>
              )}

              <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
                <div className="space-y-2">
                  <Label htmlFor="new-password">New password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    placeholder="Create a strong password"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                    <span>Password strength</span>
                    <span className="font-medium text-foreground">{passwordStrength.label}</span>
                  </div>
                  <Progress value={passwordStrength.score} className="h-2" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm new password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    placeholder="Re-enter your new password"
                    required
                  />
                </div>

                <div className="rounded-2xl border border-border/60 bg-background/60 p-4 text-xs leading-6 text-muted-foreground">
                  <div className="mb-2 flex items-center gap-2 text-foreground">
                    <LockKeyhole className="h-4 w-4 text-primary" />
                    <span className="font-medium">Security checklist</span>
                  </div>
                  <ul className="space-y-1">
                    <li>• At least 8 characters</li>
                    <li>• Includes uppercase and lowercase letters</li>
                    <li>• Includes at least one number</li>
                    <li>• Different from your previous password</li>
                  </ul>
                </div>

                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? "Updating password..." : "Update password"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
